import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Cookies from 'js-cookie';
import { COOKIES, ROUTE } from '@/lib/constants';
import { accessToken, validateToken } from '@/lib/utils/helpers';
import { useDropStore, useInventoryStore } from '@/stores';
import { dropsApi } from '@/lib/api';
import AppLoader from '@/components/AppLoader';
import { useIsAuthorized } from '@/hooks/useIsAuthorized';

export default function MainLayout(
  {
    children,
  }: React.PropsWithChildren,
): React.ReactElement {
  const isAuth = useIsAuthorized();
  const router = useRouter();

  const getInventoryDrops = useInventoryStore((state) => state.fetchInventorySingleDrops);
  const getInventoryCollections = useInventoryStore((state) => state.fetchInventoryCollections);
  const claimDrop = useDropStore((state) => state.claimDrop);
  const [isLoading, setIsLoading] = useState(router.pathname.includes('prime-gaming'));

  const shouldClaimDrop = () => {
    const queryAction = router.query.action;
    const queryDropSlug = router.query.drop_slug;
    return router.pathname === ROUTE.INVENTORY_ROUTE && queryAction === 'claim' && !!queryDropSlug;
  };

  const redirectToMainPage = () => {
    router.replace(ROUTE.INDEX);
  };

  const initApplication = async () => {
    try {
      const token = accessToken.get();
      const isTokenValid = validateToken(token);

      if (!isTokenValid) {
        if (router.pathname.startsWith(ROUTE.INVENTORY_ROUTE)) {
          Cookies.remove(COOKIES.ACCESS_TOKEN);
          redirectToMainPage();
          return;
        }

        return;
      }

      setIsLoading(true);

      await getInventoryDrops();
      await getInventoryCollections();

      if (shouldClaimDrop()) {
        await autoClaimDrop();
      }
    } catch (err) {
      redirectToMainPage();
    } finally {
      setIsLoading(false);
    }
  };

  const autoClaimDrop = async () => {
    const queryDropSlug = router.query.drop_slug as string;
    const updatedQuery = { ...router.query };

    const { status: dropClaimStatus } = await dropsApi.getDropClaimInfo(queryDropSlug);

    try {
      if (dropClaimStatus !== 'not_claimed') {
        updatedQuery['status'] = 'claimed';
        return router.replace({ pathname: ROUTE.INVENTORY_ROUTE, query: updatedQuery });
      }

      await claimDrop(queryDropSlug as string);
      updatedQuery['status'] = 'success';
      return router.replace({ pathname: ROUTE.INVENTORY_ROUTE, query: updatedQuery });
    } catch (err) {
      updatedQuery['status'] = 'failed';
      return router.replace({ pathname: ROUTE.INVENTORY_ROUTE, query: updatedQuery });
    }
  };

  useEffect(() => {
    if (router.isReady) {
      initApplication();
    }
  }, [router.isReady, isAuth]);

  if (isLoading) {
    return <AppLoader />;
  }

  return (
    <>
      {children}
    </>
  );
}
